/**
 * ViewportFrameLayer — Three.js component rendering the border, lock-badge,
 * and resize-grip markers for each paper-space VIEWPORT entity (MC3).
 *
 * Rendered INSIDE the R3F Canvas, above PaperSpaceLayer (renderOrder = 5).
 * Each viewport contributes:
 *
 *   1. A thin rectangle border (LineSegments) at the VP's paper-space
 *      position, coloured BYLAYER (resolves from the layer table using the
 *      viewport entity's layer assignment). gap2-iter7: per-VP LineBasicMaterial
 *      built from the layer's ACI color index via aciToHex().
 *   2. A small lock-indicator square at the top-right corner when
 *      `viewport.displayLocked === true`.
 *   3. Eight grip markers (4 corners + 4 midpoints) as small point meshes.
 *      Not interactive in MC3 — interaction is MC4 scope.  Rendered so
 *      users can see the resize handles and the designer can verify layout.
 *
 * Coordinate convention: `viewport.center` is in paper-space world units
 * (same coords as PaperSpaceLayer).  `paperOrigin` translates the viewport
 * rectangle into the sheet's world frame so it aligns with the paper plane.
 *
 * TODO(gap2 lineweight-by-layer): VP frame lineweight currently renders at
 * 1px (WebGL LineBasicMaterial hard limit). AutoCAD parity requires rendering
 * the frame with the thick-line material (LineMaterial/LineSegments2) at the
 * layer's lineweight converted from mm*100 enum to screen px. Wire this up
 * using the thickLines/ path in a follow-up PR once COLOR=BYLAYER is verified.
 */

import * as React from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";

import type { ViewportEntity, LayerDefinition } from "@dxf-ai-editor/drawing-model";
import { readViewportOverrides } from "@dxf-ai-editor/drawing-model";
import { aciToHex } from "../rendering/aciColorMap";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// FRAME_Z is placed well in front of the paper sheet (PAPER_Z = -0.5).
// Gap of 0.4 units exceeds WebGL depth-precision variance for orthographic views.
// depthTest:false on all frame materials is the primary z-fight guard — this
// constant is a secondary defence ensuring Z ordering is correct even in
// edge cases (e.g. clip planes, depth-prepass interactions).
const FRAME_Z = -0.1;
const GRIP_Z = -0.2;
const LOCK_Z = -0.15;

// AutoCAD parity: active VP frame (MSPACE_VIA_VP) uses a muted blue border.
// 0x00ffff (bright cyan) overpowers the drawing; 0x2277aa matches AutoCAD's
// active-viewport-edge sysvar default (dark blue at standard background).
// This overrides BYLAYER color while the viewport is in MSPACE_VIA_VP mode.
// depthTest:false ensures the frame renders above the paper sheet without
// z-fighting regardless of depth-buffer precision.
const FRAME_ACTIVE_MATERIAL = new THREE.LineBasicMaterial({
  color: 0x2277aa,
  depthWrite: false,
  depthTest: false,
});


// Filled square so gold pixels are detectable by pixel-scan tests.
// LineBasicMaterial renders only 1-px-wide edges which are inside the badge
// interior when the test box is centered on the badge center.
const LOCK_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xffcc00,
  depthWrite: false,
  depthTest: false,
  side: THREE.DoubleSide,
});

const GRIP_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0x4488ff,
  depthWrite: false,
  depthTest: false,
});

// Grip square half-size in world units.  AutoCAD grips appear ~4 px on screen;
// at 1 mm ≈ 2 px (typical paper-space zoom) a 1 mm half-size gives 4 px grips.
const GRIP_HALF = 1.0;

// Lock indicator square half-size at the top-right corner.
// At typical paper-space zoom (~4 px/mm) this yields a ~20×20 px badge.
const LOCK_HALF = 2.5;

// ---------------------------------------------------------------------------
// Helper: build rectangle LineGeometry
// ---------------------------------------------------------------------------

function buildRectLineGeometry(
  w: number,
  h: number,
  cx: number,
  cy: number,
): THREE.BufferGeometry {
  const x0 = cx - w / 2;
  const y0 = cy - h / 2;
  const x1 = cx + w / 2;
  const y1 = cy + h / 2;
  // Four edges as line pairs (GL_LINES)
  const positions = new Float32Array([
    x0, y0, 0,  x1, y0, 0, // bottom
    x1, y0, 0,  x1, y1, 0, // right
    x1, y1, 0,  x0, y1, 0, // top
    x0, y1, 0,  x0, y0, 0, // left
  ]);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geo;
}

// ---------------------------------------------------------------------------
// Helper: build grip points geometry
// ---------------------------------------------------------------------------

function buildGripPointsGeometry(
  w: number,
  h: number,
  cx: number,
  cy: number,
): THREE.BufferGeometry {
  const hw = w / 2;
  const hh = h / 2;
  // 8 grip positions: 4 corners + 4 midpoints
  const pts: number[] = [
    cx - hw, cy - hh, 0,  // BL
    cx + hw, cy - hh, 0,  // BR
    cx + hw, cy + hh, 0,  // TR
    cx - hw, cy + hh, 0,  // TL
    cx,      cy - hh, 0,  // bottom-mid
    cx + hw, cy,      0,  // right-mid
    cx,      cy + hh, 0,  // top-mid
    cx - hw, cy,      0,  // left-mid
  ];
  const geo = new THREE.BufferGeometry();
  geo.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(pts), 3),
  );
  return geo;
}

// ---------------------------------------------------------------------------
// Single VP frame
// ---------------------------------------------------------------------------

interface ViewportFrameProps {
  viewport: ViewportEntity;
  paperOrigin: { x: number; y: number };
  isActive: boolean;
  /** Layer table for BYLAYER color resolution. */
  layerMap: ReadonlyMap<string, LayerDefinition>;
  /** MC5: called when the user single-clicks the VP frame to select it. */
  onSelect?: (viewportId: string) => void;
}

function ViewportFrame({
  viewport,
  paperOrigin,
  isActive,
  layerMap,
  onSelect,
}: ViewportFrameProps): React.ReactElement {
  const { center, width, height, displayLocked } = viewport;
  const cx = center.x + paperOrigin.x;
  const cy = center.y + paperOrigin.y;

  // Effective lock = entity field OR XDATA override. AutoCAD shows the lock
  // badge when either source says locked — readViewportOverrides merges both.
  const overrides = React.useMemo(() => readViewportOverrides(viewport), [viewport]);
  const effectiveLocked = displayLocked || overrides.locked;

  // Demand-mode invalidation: changing `visible` updates the Three.js object
  // but does NOT automatically request a new WebGL frame. Call invalidate()
  // so the canvas repaints and the badge appears/disappears immediately.
  const { invalidate } = useThree();
  React.useEffect(() => { invalidate(); }, [effectiveLocked, invalidate]);

  // BYLAYER color resolution: look up the layer this VP entity sits on and
  // resolve its ACI color index to a Three.js hex number.
  //
  // AutoCAD background-aware ACI 7 rule (Autodesk ACI reference + ezdxf):
  //   ACI 7 is the "foreground" color — BLACK on light backgrounds, WHITE on
  //   dark backgrounds. Paper space always has a light (white) sheet, so ACI 7
  //   MUST map to BLACK (#000000) here. Without this override, aciToHex(7)
  //   returns #FFFFFF (white for the dark model-space UI), which is invisible
  //   on the white paper sheet. Any layer whose color resolves to white (#FFFFFF)
  //   or defaults to ACI 7 gets the same treatment.
  const frameMaterial = React.useMemo(() => {
    if (isActive) return FRAME_ACTIVE_MATERIAL;
    const layerDef = layerMap.get(viewport.layer.toLowerCase());
    const aciIndex = layerDef?.color ?? 7;
    const hexStr = aciToHex(aciIndex) ?? "#ffffff";
    // Background-aware foreground applies ONLY to ACI 7 (the designated
    // foreground index).  A layer explicitly colored white (#FFFFFF via a
    // non-7 ACI) must stay white — drop the hex-equals-white clause.
    const isAci7 = aciIndex === 7;
    const effectiveHex = isAci7 ? 0x000000 : parseInt(hexStr.replace("#", ""), 16);
    return new THREE.LineBasicMaterial({
      color: effectiveHex,
      depthWrite: false,
      depthTest: false,
    });
  }, [isActive, layerMap, viewport.layer]);
  // Dispose per-VP material on unmount (not shared; FRAME_ACTIVE_MATERIAL is a shared const).
  React.useEffect(() => {
    if (frameMaterial !== FRAME_ACTIVE_MATERIAL) {
      return () => { frameMaterial.dispose(); };
    }
  }, [frameMaterial]);

  const frameGeo = React.useMemo(
    () => buildRectLineGeometry(width, height, cx, cy),
    [width, height, cx, cy],
  );
  React.useEffect(() => () => { frameGeo.dispose(); }, [frameGeo]);

  const gripGeo = React.useMemo(
    () => buildGripPointsGeometry(width, height, cx, cy),
    [width, height, cx, cy],
  );
  React.useEffect(() => () => { gripGeo.dispose(); }, [gripGeo]);

  // Lock badge: filled PlaneGeometry quad at the top-right corner.
  // Filled mesh so interior pixels (not just 1-px edges) are gold-colored,
  // making the badge detectable by pixel-scan tests and clearly visible on screen.
  const lockGeo = React.useMemo(
    () => new THREE.PlaneGeometry(LOCK_HALF * 2, LOCK_HALF * 2),
    [],
  );
  // Lock badge world-space position (top-right VP corner).
  const lockX = cx + width / 2;
  const lockY = cy + height / 2;
  React.useEffect(() => () => { lockGeo.dispose(); }, [lockGeo]);

  // Grip geometry as instanced PlaneGeometry quads would be ideal but
  // THREE.Points is lighter and sufficient for MC3 visual verification.
  const GRIP_SIZE = GRIP_HALF * 2;
  const gripPlaneGeo = React.useMemo(
    () => new THREE.PlaneGeometry(GRIP_SIZE, GRIP_SIZE),
    [GRIP_SIZE],
  );
  React.useEffect(() => () => { gripPlaneGeo.dispose(); }, [gripPlaneGeo]);

  // Grip corner positions for instanced quads
  const gripPositions = React.useMemo(() => {
    const hw = width / 2;
    const hh = height / 2;
    return [
      [cx - hw, cy - hh], [cx + hw, cy - hh],
      [cx + hw, cy + hh], [cx - hw, cy + hh],
      [cx,      cy - hh], [cx + hw, cy     ],
      [cx,      cy + hh], [cx - hw, cy     ],
    ] as [number, number][];
  }, [cx, cy, width, height]);

  return (
    <group>
      {/* Viewport frame border — BYLAYER color; blue when MSPACE_VIA_VP active */}
      {/* MC5: single-click on the frame selects the VP entity in PSPACE */}
      <lineSegments
        geometry={frameGeo}
        material={frameMaterial}
        position={[0, 0, FRAME_Z]}
        renderOrder={5}
        onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(viewport.id); } : undefined}
      />

      {/* Lock badge — filled gold square at VP top-right corner */}
      <mesh
        geometry={lockGeo}
        material={LOCK_MATERIAL}
        position={[lockX, lockY, LOCK_Z]}
        renderOrder={6}
        visible={effectiveLocked}
      />

      {/* Resize grips — not interactive in MC3 */}
      {gripPositions.map(([gx, gy], i) => (
        <mesh
          key={i}
          geometry={gripPlaneGeo}
          material={GRIP_MATERIAL}
          position={[gx, gy, GRIP_Z]}
          renderOrder={7}
        />
      ))}
    </group>
  );
}

// ---------------------------------------------------------------------------
// ViewportFrameLayer (exported)
// ---------------------------------------------------------------------------

export interface ViewportFrameLayerProps {
  viewports: ViewportEntity[];
  /**
   * Paper-space sheet origin in the renderer's OFFSET-world frame
   * (`Scene3DStage`'s `paperOriginOffset`), NOT the raw
   * `LayoutPageSetup.origin` — the VP frames must land on the same pixels as
   * the offset-world sheet mesh. Defaults to (0, 0), which is the correct
   * offset-world value only when no session origin is locked.
   */
  paperOrigin?: { x: number; y: number };
  /** Id of the viewport currently active in MSPACE_VIA_VP mode. Null = none. */
  activeViewportId?: string | null;
  /**
   * Layer table for BYLAYER color resolution (gap2-iter7). Keyed by
   * lower-cased layer name. Pass `styleTables.layers` from the editor store
   * (as a `ReadonlyMap<string, LayerDefinition>` built from the array).
   * When absent each VP frame falls back to white (ACI 7).
   */
  layerMap?: ReadonlyMap<string, LayerDefinition>;
  /**
   * MC5 — optional click-selection handler. When provided, a single click on
   * the viewport frame border dispatches a select event with the VP id.
   * Used by Scene3DStage to dispatch shell/select-entities and show the
   * ViewportInspector panel.
   */
  onSelectViewport?: (viewportId: string) => void;
}

const DEFAULT_ORIGIN = { x: 0, y: 0 };
const EMPTY_LAYER_MAP: ReadonlyMap<string, LayerDefinition> = new Map();

/**
 * Render frame borders, lock badges, and non-interactive resize grips for
 * all viewports on the active layout.  Mount inside the R3F Canvas, after
 * `<PaperSpaceLayer>`.
 */
export function ViewportFrameLayer({
  viewports,
  paperOrigin = DEFAULT_ORIGIN,
  activeViewportId = null,
  layerMap = EMPTY_LAYER_MAP,
  onSelectViewport,
}: ViewportFrameLayerProps): React.ReactElement {
  return (
    <group userData={{ isViewportOverlay: true, isViewportFrame: true }}>
      {viewports.map((vp) => (
        <ViewportFrame
          key={vp.id}
          viewport={vp}
          paperOrigin={paperOrigin}
          isActive={vp.id === activeViewportId}
          layerMap={layerMap}
          onSelect={onSelectViewport}
        />
      ))}
    </group>
  );
}
